import { CONTROL_FOCUS_CLASS, CONTROL_OPEN_CLASS } from "../lib/controlStyles";
import { forwardRef } from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";

import { cn } from "../lib/cn";

const EMPTY_VALUE = "__nexus_select_empty__";

export interface SelectOption {
  value: string;
  label: string;
  /** Muted text right-aligned in the list only; the trigger shows the label. */
  detail?: string;
  disabled?: boolean;
}

export interface SelectProps {
  label: string;
  value: string;
  options: readonly SelectOption[];
  onValueChange: (value: string) => void;
  placeholder?: string;
  emptyLabel?: string;
  controlSize?: "sm" | "md";
  disabled?: boolean;
  className?: string;
}

const controlSizeClasses = {
  sm: "density-control-sm text-xs",
  md: "density-control-md text-sm",
} as const;

export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
  {
    label,
    value,
    options,
    onValueChange,
    placeholder = "Select an option",
    emptyLabel,
    controlSize = "md",
    disabled = false,
    className,
  },
  ref,
) {
  if (options.some((option) => option.value === EMPTY_VALUE)) {
    throw new Error(`Select option value "${EMPTY_VALUE}" is reserved.`);
  }

  const radixValue = emptyLabel && value === "" ? EMPTY_VALUE : value;

  return (
    <SelectPrimitive.Root
      value={radixValue}
      disabled={disabled}
      onValueChange={(nextValue) => onValueChange(nextValue === EMPTY_VALUE ? "" : nextValue)}
    >
      <SelectPrimitive.Trigger
        ref={ref}
        aria-label={label}
        className={cn(
          "flex w-full items-center justify-between gap-2 rounded-control border border-control-border bg-surface px-3 text-primary transition-colors",
          "hover:border-control-border-hover",
          CONTROL_OPEN_CLASS,
          CONTROL_FOCUS_CLASS,
          "disabled:cursor-not-allowed disabled:bg-surface-elevated disabled:text-muted disabled:opacity-70",
          controlSizeClasses[controlSize],
          className,
        )}
      >
        <span className="min-w-0 flex-1 truncate whitespace-nowrap text-left">
          <SelectPrimitive.Value placeholder={placeholder} />
        </span>
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted" aria-hidden />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          data-nexus-select-content=""
          position="popper"
          sideOffset={6}
          collisionPadding={8}
          className={cn(
            "z-80 max-h-[min(20rem,var(--radix-select-content-available-height))] min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-control border border-border bg-surface shadow-popover",
            "data-[state=open]:animate-in data-[state=closed]:animate-out motion-reduce:animate-none",
          )}
        >
          <SelectPrimitive.Viewport className="p-1">
            {emptyLabel ? <SelectItem value={EMPTY_VALUE} label={emptyLabel} /> : null}
            {options.map((option) => (
              <SelectItem key={option.value} {...option} />
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
});

function SelectItem({ value, label, detail, disabled }: SelectOption) {
  return (
    <SelectPrimitive.Item
      value={value}
      disabled={disabled}
      className={cn(
        "density-control-sm relative flex cursor-default select-none items-center rounded-md pr-8 pl-2.5 text-sm text-primary outline-none transition-colors",
        "focus:bg-surface-elevated data-disabled:pointer-events-none data-disabled:opacity-50",
      )}
    >
      <SelectPrimitive.ItemText>{label}</SelectPrimitive.ItemText>
      {detail ? (
        <span className="ml-auto whitespace-nowrap pl-4 text-xs tabular-nums text-muted">
          {detail}
        </span>
      ) : null}
      <SelectPrimitive.ItemIndicator className="absolute right-2.5 inline-flex items-center">
        <Check className="h-3.5 w-3.5 text-interaction-text" strokeWidth={2.5} aria-hidden />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
}
